import { DatePipe } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, DEFAULT_CURRENCY_CODE, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { BugsnagErrorHandler } from '@bugsnag/plugin-angular/dist/esm2015';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { Store, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { take } from 'rxjs/operators';
import { CartModule } from 'src/app/feature/cart/cart.module';
import { SwapLanguageModule } from 'src/app/feature/swap-language/swap-language.module';
import { ProfileOverviewModule } from 'src/app/pages/profile-overview/profile-overview.module';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';

import { AppComponent } from './app.component';
import { selectAppConfig } from './core/application-config/application-config.selectors';
import { CoreModule } from './core/core.module';
import { LocaleIdService } from './core/locale/locale-id.service';
import { LocaleModule } from './core/locale/locale.module';
import { NotificationModule } from './core/notification/notification.module';
import { LayoutModule } from './feature/layout/layout.module';
import { SpinnerModule } from './feature/spinner/spinner.module';
import { ToastModule } from './feature/toast/toast.module';
import { BookingsEffects } from './pages/booking-portal/redux/bookings.effects';
import { bookingsReducer } from './pages/booking-portal/redux/bookings.reducer';
import { RecoverModule } from './pages/recover/recover.module';
import { VerifyModule } from './pages/verify/verify.module';

const { production } = environment;

/**
 * Creates a new bugsnag error handler
 */
export function errorHandlerFactory() {
	return new BugsnagErrorHandler();
}

function merchantCurrencyCode(store: Store) {
	let currency = 'USD';
	store
		.select(selectAppConfig(['currency_code']))
		.pipe(take(1))
		.subscribe((data) => {
			currency = data.currency_code || 'USD';
		});

	return currency;
}

@NgModule({
	declarations: [AppComponent],
	imports: [
		BrowserModule,
		AppRoutingModule,
		BrowserAnimationsModule,
		StoreModule.forRoot([]),
		StoreRouterConnectingModule.forRoot(),
		EffectsModule.forRoot([]),
		StoreModule.forFeature('bookings', bookingsReducer),
		CartModule,
		EffectsModule.forFeature([BookingsEffects]),
		StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: production }),
		CoreModule,
		RouterModule,
		SpinnerModule,
		LocaleModule,
		RecoverModule,
		VerifyModule,
		SwapLanguageModule,
		ToastModule,
		NotificationModule,
		LayoutModule,
		ProfileOverviewModule,
	],
	providers: [
		DatePipe,
		{
			provide: LOCALE_ID,
			useClass: LocaleIdService,
		},
		{
			provide: ErrorHandler,
			useFactory: errorHandlerFactory,
		},
		{
			provide: DEFAULT_CURRENCY_CODE,
			useFactory: merchantCurrencyCode,
			deps: [Store],
		},
	],
	bootstrap: [AppComponent],
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
